import React from "react";
import { useBlogPosts } from "../../../../Hooks/useBlogPost";
import LoaderSpinner from "../../../../util/LoadingComponent/LoaderSpinner";
import { Link } from "react-router-dom";
import styles from "./BlogListPage.module.scss";
import PageSEO from "../../../../Components/SEO/PageSEO";

const BlogListPage = () => {
  const { posts, loading, message, error } = useBlogPosts();

  // Helper function to strip HTML tags for clean article body text
  const stripHtml = (html) => {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, ' ') // Replace HTML tags with space
      .replace(/&nbsp;/g, ' ')  // Replace &nbsp; with space
      .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
      .trim();                  // Remove leading/trailing spaces
  };

  // Define structured data for the blog list
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "WebNX Blog",
    "description": "Latest news, insights, and updates about enterprise hosting, dedicated servers, and data center infrastructure from WebNX.",
    "url": "https://webnx.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "WebNX",
      "logo": {
        "@type": "ImageObject",
        "url": "https://webnx.com/images/logo.png"
      }
    },
    "blogPost": posts?.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.createdAt,
      "dateModified": post.createdAt,
      "url": `https://webnx.com/blog/${post.slug}`,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://webnx.com/blog/${post.slug}`
      },
      "publisher": {
        "@type": "Organization",
        "name": "WebNX",
        "logo": {
          "@type": "ImageObject",
          "url": "https://webnx.com/images/logo.png"
        }
      },
      "author": {
        "@type": "Organization",
        "name": "WebNX"
      }
    })) || [],
    "inLanguage": "en-US",
    "isAccessibleForFree": "True",
    "keywords": [
      "enterprise hosting",
      "dedicated servers",
      "data center infrastructure",
      "colocation services",
      "server technology",
      "hosting solutions",
      "WebNX updates",
      "tech insights"
    ]
  };

  if (loading) return <LoaderSpinner />;
  if (error) return <div className={styles.error}>Error: {message}</div>;

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <>
      <PageSEO
        title="WebNX Blog | Enterprise Hosting Insights & Tech Updates"
        description="Stay informed with the latest insights on enterprise hosting, dedicated servers, and data center infrastructure. Expert articles on hosting solutions, technology trends, and industry updates."
        keywords="WebNX blog, enterprise hosting blog, dedicated server insights, data center news, hosting technology, server infrastructure, tech updates, hosting industry news, WebNX updates"
        path="/blog"
        image="https://webnx.com/assets/blog-header.jpg"
        schema={blogListSchema}
        socialProfiles={{
          facebook: "https://www.facebook.com/WebNX/",
          linkedin: "https://www.linkedin.com/company/webnx/"
        }}
      />

      <div className={styles.blogContainer}>
        <div className={styles.header}>
          <h1>What's New?</h1>
          <p className={styles.subtitle}>
            The latest news, insights, and updates from WebNX.
          </p>
        </div>

        <div className={styles.blogGrid}>
          {posts.map((post) => (
            <article key={post._id} className={styles.blogCard}>
              <div className={styles.cardContent}>
                <h2>{post.title}</h2>
                <div className={styles.date}>
                  {formatDate(post.createdAt)}
                </div>
                <p className={styles.excerpt}>
                  {post.excerpt}...
                </p>
              </div>
              <div className={styles.cardFooter}>
                <Link 
                  to={`/blog/${post.slug}`} 
                  state={{ post }} 
                  className={styles.readMore}
                >
                  Read more
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </>
  );
};

export default BlogListPage;
